import React, { useState, useEffect } from 'react';
import styles from '../static/styles/Leaderboard.module.css';
import { fetchLeaderboard } from '../api/leaderboard';
import { trackSectionView } from '../utils/analytics';
import crownIcon from '../static/img/Leaderboard/iconCrown.svg';
import defaultAvatar from '../static/img/Sidebar/Avatar.png';
import ArrowLeft from '../static/img/backArrow.png'
import { useNavigate } from 'react-router-dom';

const trimText = (text, maxLength = 12) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '..' : text;
};

const Leaderboard = () => {
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedPlayer, setExpandedPlayer] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        trackSectionView('leaderboard');
        document.title = 'Leaderboard | Jazz Esl';
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await fetchLeaderboard();
                setLeaderboardData(data);
            } catch (err) {
                setError(err.message || 'Failed to load leaderboard');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) return <div className={styles.loading}>Loading...</div>;
    if (error) return <div className={styles.error}>Error: {error}</div>;

    const top3 = leaderboardData.slice(0, 3);
    const rest = leaderboardData.slice(3);

    // Classic podium order: 2nd | 1st | 3rd
    const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);

    const getPosition = (player) => {
        if (player === top3[0]) return 1;
        if (player === top3[1]) return 2;
        return 3;
    };

    const toggleExpand = (name) => {
        setExpandedPlayer(prev => prev === name ? null : name);
    };

    return (
        <div className={styles.container}>
            <div className={styles.navigationHeader}>
                <div
                    className={styles.navigationBack}
                    onClick={() => navigate(-1)}
                >
                    <img src={ArrowLeft} alt="<" />
                </div>
                <div className={styles.navigationTitle}>
                    <p>players</p>
                    <h2 className={styles.heading}>leaderboard</h2>
                </div>
            </div>

            {top3.length > 0 && (
                <div className={styles.podium}>
                    {podiumOrder.map((player) => {
                        const pos = getPosition(player);
                        const isFirst = pos === 1;
                        return (
                            <div
                                key={player.name}
                                className={`${styles.podiumCard} ${
                                    isFirst ? styles.podiumFirst
                                    : pos === 2 ? styles.podiumSecond
                                    : styles.podiumThird
                                }`}
                            >
                                {isFirst && (
                                    <img src={crownIcon} alt="Crown" className={styles.crown} />
                                )}
                                <div className={`${styles.avatarRing} ${
                                    pos === 1 ? styles.ringGold
                                    : pos === 2 ? styles.ringSilver
                                    : styles.ringBronze
                                }`}>
                                    <img
                                        src={player.profileImage || defaultAvatar}
                                        alt={player.name}
                                        className={styles.podiumAvatar}
                                        onError={e => { e.target.onerror = null; e.target.src = defaultAvatar; }}
                                    />
                                </div>
                                <div className={styles.rankBadge}>
                                    {pos === 1 ? '🥇' : pos === 2 ? '🥈' : '🥉'}
                                </div>
                                <p className={styles.podiumName}>{trimText(player.name, 11)}</p>
                                <p className={styles.podiumScore}>{player.score?.toLocaleString()}</p>
                                <p className={styles.podiumCreditsLabel}>credits</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {rest.length > 0 && (
                <div className={styles.list}>
                    <div className={styles.listHeader}>
                        <span className={`${styles.listCol} ${styles.colRank}`}>#</span>
                        <span className={`${styles.listCol} ${styles.colPlayer}`}>Player</span>
                        <span className={`${styles.listCol} ${styles.colStat} ${styles.desktopOnly}`}>Tournaments</span>
                        <span className={`${styles.listCol} ${styles.colStat} ${styles.desktopOnly}`}>Challenges</span>
                        <span className={`${styles.listCol} ${styles.colScore}`}>Credits</span>
                        <span className={`${styles.listCol} ${styles.colChevron}`}></span>
                    </div>

                    {rest.map((player, index) => (
                        <div key={player.name} className={styles.listItem}>
                            <div
                                className={`${styles.listRow} ${expandedPlayer === player.name ? styles.listRowExpanded : ''}`}
                                onClick={() => toggleExpand(player.name)}
                            >
                                <span className={`${styles.listRank} ${styles.colRank}`}>{index + 4}</span>
                                <div className={`${styles.listPlayer} ${styles.colPlayer}`}>
                                    <img
                                        src={player.profileImage || defaultAvatar}
                                        alt={player.name}
                                        className={styles.listAvatar}
                                        onError={e => { e.target.onerror = null; e.target.src = defaultAvatar; }}
                                    />
                                    <div className={styles.listPlayerInfo}>
                                        <span className={styles.listName}>{trimText(player.name, 18)}</span>
                                        <span className={styles.listUsername}>
                                            @{trimText(player.username || player.name.toLowerCase().replace(/\s+/g, ''), 18)}
                                        </span>
                                    </div>
                                </div>
                                <span className={`${styles.listStat} ${styles.colStat} ${styles.desktopOnly}`}>
                                    {player.total_tournaments ?? '—'}
                                </span>
                                <span className={`${styles.listStat} ${styles.colStat} ${styles.desktopOnly}`}>
                                    {player.total_challenges ?? '—'}
                                </span>
                                <span className={`${styles.listScore} ${styles.colScore}`}>
                                    {player.score?.toLocaleString()}
                                </span>
                                <span className={`${styles.chevron} ${styles.colChevron} ${expandedPlayer === player.name ? styles.chevronOpen : ''}`}>
                                    ›
                                </span>
                            </div>

                            {expandedPlayer === player.name && (
                                <div className={styles.expandedPanel}>
                                    <div className={styles.expandedStat}>
                                        <span className={styles.expandedStatLabel}>Credits</span>
                                        <span className={styles.expandedStatValue}>{player.score?.toLocaleString()}</span>
                                    </div>
                                    <div className={styles.expandedStat}>
                                        <span className={styles.expandedStatLabel}>Tournaments</span>
                                        <span className={styles.expandedStatValue}>{player.total_tournaments ?? '—'}</span>
                                    </div>
                                    <div className={styles.expandedStat}>
                                        <span className={styles.expandedStatLabel}>Prizes Redeemed</span>
                                        <span className={styles.expandedStatValue}>{player.redeemed_items ?? '—'}</span>
                                    </div>
                                    <div className={styles.expandedStat}>
                                        <span className={styles.expandedStatLabel}>Challenges</span>
                                        <span className={styles.expandedStatValue}>{player.total_challenges ?? '—'}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Leaderboard;
